import { useState, useEffect, useRef } from "react";
import * as Sentry from "../../lib/errorReporting";
import { supabase } from "../../lib/supabase";
import { gradingLeague, betDisplayStatus, calcStatus, SM, fmtOdds, isNotGradedLegOnFinal } from "../../lib/betting";
import { rowToBet } from "../../lib/storage";
import { getSportsbook } from "../../lib/sportsbooks";
import { copyText } from "../../lib/platform";
import { JT } from "../../components/ui/jumbotronTokens";
import { readFeatureConsent, recordFeatureConsent, forgetFeatureConsent } from "../onboarding/featureConsent";
import BookiesConsentSheet from "./BookiesConsentSheet";
import { accessDeniedMessage } from "../../lib/accessCopy";
import BookiesSafetyActions from "./BookiesSafetyActions";
import { fetchMyBlocks } from "./bookiesSafety";

// #1353 PR 3b (r5b.4 I1): client grading trusts the server-written DNP flag.
const TRUST_DNP = { trustDnpFlag: true };

export default function BookiesPage({user, games}) {
  const [groups,    setGroups]    = useState([]);
  const [boardSlips,setBoardSlips]= useState([]);
  const [loading,   setLoading]   = useState(true);
  const [createOpen,setCreateOpen]= useState(false);
  const [joinOpen,  setJoinOpen]  = useState(false);
  const [newName,   setNewName]   = useState("");
  const [newEmoji,  setNewEmoji]  = useState("🏒");
  const [joinCode,  setJoinCode]  = useState("");
  const [joinError, setJoinError] = useState("");
  const [creating,  setCreating]  = useState(false);
  const [joining,   setJoining]   = useState(false);
  const [copied,    setCopied]    = useState(null);
  const [loadError, setLoadError] = useState(false);
  // #1503 PR 2: the RPCs refuse an account the owner has not approved (ST025). An expected state, not an error.
  const [approvalPending, setApprovalPending] = useState(false);
  const [createError, setCreateError] = useState("");
  // #1421: sharing needs the user's Bookies consent. 'checking' | 'granted' | 'missing' | 'declined' | 'error', plus
  // (#1731 B2, Nova P2-1) 'outdated-review' / 'outdated': the user agreed to an earlier version, so the server still
  // shares their bets. They see the updated sheet once ('outdated-review'); "Not now" keeps the full page with a banner
  // that says they share under the earlier terms ('outdated'), never "haven't agreed" or the limited view.
  const [consent,   setConsent]   = useState("checking");
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [myBlocks,  setMyBlocks]  = useState(() => new Set());
  // True once the board payload carries members[].sharing, i.e. the server enforces consent and blocks (PR 2b).
  // Until then Block is not offered, so the page never promises hiding the server does not do yet.
  const [blocksEnforced, setBlocksEnforced] = useState(false);
  // Bumped per user (and per retry): a consent or blocks result that arrives for an earlier user is dropped.
  const genRef = useRef(0);
  // Per-request sequence for the board and the block list: overlapping refreshes (two safety writes in a row) commit
  // only the newest response, so an older one resolving last cannot overwrite newer state (Codex r2 P1).
  const loadSeqRef = useRef(0);
  const blocksSeqRef = useRef(0);
  // The user id the consent / board / blocks state belongs to. On a direct account switch the first render with
  // the new user still holds the previous user's committed state (the reset effect runs after it), so nothing
  // user-scoped renders until this matches the current user (Codex r1 P1).
  const [stateOwner, setStateOwner] = useState(null);
  // The current user id for async completions: a safety write's onChanged runs from its click-time closure, which
  // can belong to the previous user after an account switch.
  const userIdRef = useRef(user?.id ?? null);
  userIdRef.current = user?.id ?? null;

  // Keyed on the user id, not the object: useAuth re-delivers the same user as a new object on TOKEN_REFRESHED
  // (CC-IMPL-021), which must not reset the page or drop an in-flight agreement (pre-Codex FC F2).
  useEffect(()=>{
    const gen = ++genRef.current;
    setConsent("checking"); setConsentError(null); setConsentBusy(false); setMyBlocks(new Set());
    setGroups([]); setBoardSlips([]); setBlocksEnforced(false); setLoadError(false); setApprovalPending(false);
    setCreateOpen(false); setJoinOpen(false); setCreateError(""); setJoinError("");
    setStateOwner(user?.id ?? null);
    if (!user){setLoading(false);return;}
    if (!supabase){setLoading(false);setConsent("error");return;}
    load(gen);          // every consent state reads the board: members who have not agreed still see their booths
    checkConsent(gen);
  },[user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkConsent(gen) {
    try {
      const c = await readFeatureConsent(supabase, user.id, "bookies", { distinguishOutdated: true });
      if (gen !== genRef.current) return;
      setConsent(c === "granted" ? "granted" : c === "outdated" ? "outdated-review" : "missing");
      if (c === "granted" || c === "outdated") refreshBlocks(gen);
    } catch {
      if (gen !== genRef.current) return;
      setConsent("error");
      Sentry.captureMessage("BookiesPage consent read failed", { level: "warning", tags: { function: "BookiesPage.consent" } });
    }
  }

  // The server already hides blocked members' bets; this set only labels them and offers Unblock, so a failed
  // read keeps the page (CC-IMPL-037: a refresh retries).
  async function refreshBlocks(gen = genRef.current) {
    const seq = ++blocksSeqRef.current;
    const current = () => gen === genRef.current && seq === blocksSeqRef.current;
    try {
      const set = await fetchMyBlocks(supabase, user.id);
      if (current()) setMyBlocks(set);
    } catch {
      if (current()) Sentry.captureMessage("BookiesPage blocks read failed", { level: "warning", tags: { function: "BookiesPage.consent" } });
    }
  }

  function retryAll() {
    const gen = ++genRef.current;
    setConsent("checking");
    if (!supabase) { setConsent("error"); return; }
    load(gen);
    checkConsent(gen);
  }

  async function agree() {
    const gen = genRef.current;
    setConsentBusy(true); setConsentError(null);
    let r;
    try { r = await recordFeatureConsent(supabase, user.id, "bookies"); }
    // recordFeatureConsent never throws by contract, so a throw is a programming error: exception (CC-IMPL-037).
    catch (err) { r = "failed"; Sentry.captureException(err, { tags: { function: "BookiesPage.consent", stage: "save" } }); }
    if (gen !== genRef.current) return;
    setConsentBusy(false);
    if (r === "saved") { setConsent("granted"); refreshBlocks(gen); }
    else if (r === "rejected") setConsentError("Couldn't save your choice. Try again later.");
    else setConsentError("We couldn't save your choice. Check your connection and try again.");
  }

  function reload() {
    load();
    if (consent === "granted" || consent === "outdated" || consent === "outdated-review") refreshBlocks();
  }

  // A safety write succeeded. A block/unblock result is confirmed by the server, so it is applied here before the
  // refresh: a failed block-list read must not leave the member's old state on screen (Codex r3 P1). reload()'s
  // refreshBlocks bumps the sequence, so an older list read still in flight (it predates this write) is dropped.
  // A result for an earlier user is dropped.
  function onSafetyChange(change) {
    if (!change || change.userId !== userIdRef.current) return;
    if (change.memberId != null) {
      setMyBlocks((prev) => {
        const next = new Set(prev);
        if (change.blocked) next.add(change.memberId); else next.delete(change.memberId);
        return next;
      });
    }
    reload();
  }

  // create_booth / join_booth refuse a caller without a Bookies consent (ST022, PR 2b): the local "granted" was
  // stale, so forget it and ask again. Never a generic error.
  function consentLost() {
    forgetFeatureConsent(user.id, "bookies");
    setConsent("missing");
  }

  // #1053: one SECURITY DEFINER read. RLS on groups/group_members/bets/bet_legs is
  // own-rows, so the old direct queries returned a group of ONE for everyone; the RPC
  // checks membership server-side and returns an explicit column allow-list. The client
  // keeps rowToBet + calcStatus -- the #1052 contract (rows win, JSONB never read when rows
  // exist) is untouched because the payload carries bet_legs rows and never bets.legs.
  async function load(gen = genRef.current) {
    const seq = ++loadSeqRef.current;
    const current = () => gen === genRef.current && seq === loadSeqRef.current;
    let rendered = null;  // the booths this load rendered (joinBooth reads it); null on every other path
    setLoading(true); setLoadError(false);
    try {
      const { data, error } = await supabase.rpc("bookies_board");
      // A board for an earlier user (account switch mid-load) or an older refresh never renders.
      if (!current()) return;
      if (error && error.code === "ST025") {
        setGroups([]); setBoardSlips([]); setApprovalPending(true); setLoading(false);
        return;
      }
      if (error) throw Object.assign(new Error("bookies_board failed"), { cause: error, code: error.code });
      const board = data || {};
      const groupsOut = (board.groups || []).map(g => ({
        id: g.id, name: g.name, avatar_emoji: g.avatar_emoji, invite_code: g.invite_code,
        memberCount: Number(g.member_count) || 0,
        // [uid, {won,total,profit,sharing}] pairs, profit desc. A missing `sharing` key (a server before PR 2b)
        // means sharing, so stats render as before (#1421 Review Focus 4).
        leaderboard: (g.members || [])
          .map(m => [m.user_id, { won: Number(m.won) || 0, total: Number(m.total) || 0, profit: Number(m.profit) || 0, sharing: m.sharing !== false }])
          .sort((a, b) => b[1].profit - a[1].profit),
      }));
      const legsByBet = new Map();
      for (const l of (board.active_bet_legs || [])) {
        const k = String(l.bet_id);
        if (!legsByBet.has(k)) legsByBet.set(k, []);
        legsByBet.get(k).push(l);
      }
      const slips = (board.active_bets || []).map(r => ({
        ...rowToBet(r, legsByBet.get(String(r.id)) || null),
        userId: r.user_id ?? null,   // board-only: drives the YOU pill; rowToBet does not map it
      }));
      setGroups(groupsOut);
      setBoardSlips(slips);
      setBlocksEnforced((board.groups || []).some(g => (g.members || []).some(m => Object.prototype.hasOwnProperty.call(m, "sharing"))));
      rendered = groupsOut;
    } catch (loadErr) {
      if (!current()) return;
      setGroups([]); setBoardSlips([]); setLoadError(true);
      Sentry.captureException(loadErr, {
        tags: { function: 'BookiesPage.load', failureStage: 'bookies-load' },
        extra: { userId: user?.id, code: loadErr?.code ?? null },
      });
    }
    if (current()) setLoading(false);
    return rendered;
  }

  // Fixed user-facing strings per call site + SQLSTATE; the raw DB message is never rendered.
  const CREATE_MSG = { "22023": "Check the name/emoji and try again" };
  const JOIN_MSG   = { "P0002": "Invalid code — double check and try again", "22023": "Invalid code — double check and try again",
                       "ST026": "Too many tries today — try again tomorrow" };

  async function createBooth() {
    if (!newName.trim()) return;
    setCreating(true); setCreateError("");
    // A result for an earlier account (switch mid-request) is dropped whole (#1503 PR 2 pre-Codex FC F1).
    const gen = genRef.current;
    try {
      const { error } = await supabase.rpc("create_booth", { p_name: newName.trim(), p_emoji: newEmoji });
      if (gen !== genRef.current) { setCreating(false); return; }
      if (error && error.code === "ST022") { consentLost(); setCreateOpen(false); setCreating(false); return; }
      if (error && error.code === "ST025") { setApprovalPending(true); setCreateOpen(false); setCreating(false); return; }
      if (error && error.code === "22023" && error.message === "name not allowed") {
        // The exact server message (plan E8): matched, never rendered.
        setCreateError("That name isn't allowed. Pick another.");
        Sentry.captureMessage("BookiesPage.createBooth name refused", { level: "warning", tags: { function: "BookiesPage.createBooth" } });
        setCreating(false); return;
      }
      if (error) {
        // CC-IMPL-037: a mapped SQLSTATE is a user-input rejection (recoverable -> warning);
        // anything else (40001 allocation exhausted, PGRST202 function missing, 42501) is
        // terminal for this call -> captureException. The raw message is never rendered.
        const known = CREATE_MSG[error.code];
        setCreateError(known || "Couldn't create the booth — try again");
        if (known) {
          Sentry.captureMessage("BookiesPage.createBooth rejected", { level: "warning", tags: { function: "BookiesPage.createBooth" }, extra: { code: error.code ?? null } });
        } else {
          Sentry.captureException(Object.assign(new Error("create_booth failed"), { cause: error, code: error.code }), { tags: { function: "BookiesPage.createBooth" }, extra: { code: error.code ?? null } });
        }
      } else {
        setCreateOpen(false); setNewName(""); setNewEmoji("🏒"); load();
      }
    } catch (err) {
      if (gen !== genRef.current) { setCreating(false); return; }  // a throw for an earlier account (pre-Codex delta)
      setCreateError("Couldn't create the booth — try again");
      Sentry.captureException(err, { tags: { function: "BookiesPage.createBooth" } });
    }
    setCreating(false);
  }

  async function joinBooth() {
    if (!joinCode.trim()) return;
    setJoining(true); setJoinError("");
    // A result for an earlier account (switch mid-request) is dropped whole (#1503 PR 2 pre-Codex FC F1).
    const gen = genRef.current;
    try {
      const code = joinCode.trim().toUpperCase();
      const { error } = await supabase.rpc("join_booth", { p_code: code });
      if (gen !== genRef.current) { setJoining(false); return; }
      if (error && error.code === "ST022") { consentLost(); setJoinOpen(false); setJoining(false); return; }
      if (error && error.code === "ST025") { setApprovalPending(true); setJoinOpen(false); setJoining(false); return; }
      // Readiness B2: join_booth answers a hit and a wrong code the same way (no rows), so a forced rollback teaches
      // a guesser nothing. Joined = the reloaded board shows a booth with this code. A failed or superseded load
      // (null) closes the sheet; the board shows its own error.
      let groupsNow = null;
      if (!error) {
        groupsNow = await load(gen);
        if (gen !== genRef.current) { setJoining(false); return; }
      }
      const noMatch = !error && Array.isArray(groupsNow) && !groupsNow.some(g => g.invite_code === code);
      if (error || noMatch) {
        const known = JOIN_MSG[noMatch ? "P0002" : error.code];
        setJoinError(known || "Couldn't join the booth — try again");
        if (known) {
          Sentry.captureMessage("BookiesPage.joinBooth rejected", { level: "warning", tags: { function: "BookiesPage.joinBooth" }, extra: { code: noMatch ? "no_match" : (error.code ?? null) } });
        } else {
          Sentry.captureException(Object.assign(new Error("join_booth failed"), { cause: error, code: error.code }), { tags: { function: "BookiesPage.joinBooth" }, extra: { code: error.code ?? null } });
        }
      } else {
        setJoinOpen(false); setJoinCode("");
      }
    } catch (err) {
      if (gen !== genRef.current) { setJoining(false); return; }  // a throw for an earlier account (pre-Codex delta)
      setJoinError("Couldn't join the booth — try again");
      Sentry.captureException(err, { tags: { function: "BookiesPage.joinBooth" } });
    }
    setJoining(false);
  }

  const MEDALS  = ["🥇","🥈","🥉"];

  if (!user) return (
    <div style={{padding:"60px 24px",textAlign:"center",animation:"fadeUp .3s ease"}}>
      <div style={{fontSize:52,marginBottom:16}}>🏟</div>
      <div style={{fontSize:20,fontWeight:900,color:JT.text.primary,marginBottom:8}}>Sign in to join The Bookies</div>
      <div style={{fontSize:13,color:JT.text.secondary,lineHeight:1.6}}>Create booths, track your crew's slips, and see who's running the board.</div>
    </div>
  );

  const contactLine = (
    <div data-contact-line style={{fontSize:12,color:JT.text.tertiary,textAlign:"center",marginTop:16}}>Problem with a booth or member? Email <a href="mailto:hello@sliptrack.net" style={{display:"inline-flex",alignItems:"center",minHeight:44,color:JT.text.secondary,fontWeight:600}}>hello@sliptrack.net</a></div>
  );

  // #1503 PR 2: an account the owner has not approved sees why, not an error or an empty board.
  if (approvalPending && stateOwner === user.id) return (
    <div style={{padding:"20px 16px",animation:"fadeUp .3s ease"}}>
      <div data-testid="bookies-approval-pending" style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"24px",textAlign:"center"}}>
        <div style={{fontSize:14,fontWeight:800,color:JT.text.primary,marginBottom:6}}>The Bookies</div>
        <div style={{fontSize:13,color:JT.text.secondary,lineHeight:1.5}}>{accessDeniedMessage("not_found")}</div>
      </div>
    </div>
  );

  if (consent === "checking" || stateOwner !== user.id) return (
    <div style={{padding:"20px 16px"}}>
      {[1,2,3].map(i=><div key={i} className="skeleton" style={{height:72,borderRadius:12,marginBottom:8,opacity:.5}}/>)}
      {contactLine}
    </div>
  );

  if (consent === "error") return (
    <div style={{padding:"20px 16px",animation:"fadeUp .3s ease"}}>
      <div style={{background:JT.bg.well,border:`1px solid ${JT.status.lost.border}`,borderRadius:12,padding:"24px",textAlign:"center"}}>
        <div style={{fontSize:14,fontWeight:800,color:JT.text.primary,marginBottom:6}}>Couldn't load The Bookies</div>
        <div style={{fontSize:12,color:JT.text.secondary,marginBottom:12}}>Check your connection and try again.</div>
        <button onClick={retryAll} style={{background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.secondary,borderRadius:8,fontSize:12,fontWeight:700,padding:"8px 16px",minHeight:44,cursor:"pointer",fontFamily:"inherit"}}>Retry</button>
      </div>
      {contactLine}
    </div>
  );

  if (consent === "missing" || consent === "declined" || consent === "outdated-review") return (
    <div style={{padding:"20px 16px",animation:"fadeUp .3s ease"}}>
      {consent !== "declined" ? (
        <BookiesConsentSheet onAgree={agree} onDecline={()=>{setConsent(consent === "outdated-review" ? "outdated" : "declined");setConsentError(null);}} busy={consentBusy} error={consentError}/>
      ) : (
        <div style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"14px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{fontSize:13,color:JT.text.secondary,lineHeight:1.4}}>You haven't agreed to share your bets with your booth yet.</div>
          <button onClick={()=>setConsent("missing")} style={{flexShrink:0,background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.primary,borderRadius:8,fontSize:12,fontWeight:800,padding:"0 14px",minHeight:44,cursor:"pointer",fontFamily:"inherit"}}>Review</button>
        </div>
      )}
      <div style={{...JT.type.label,color:JT.text.tertiary,letterSpacing:"2px",marginBottom:12}}>YOUR BOOTHS</div>
      {loading ? (
        [1,2].map(i=><div key={i} className="skeleton" style={{height:56,borderRadius:12,marginBottom:8,opacity:.5}}/>)
      ) : loadError ? (
        <div style={{background:JT.bg.well,border:`1px solid ${JT.status.lost.border}`,borderRadius:12,padding:"16px",textAlign:"center"}}>
          <div style={{fontSize:13,color:JT.status.lost.color,marginBottom:10}}>Couldn't reach The Bookies. Try again in a moment.</div>
          <button onClick={()=>load()} style={{background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.secondary,borderRadius:8,fontSize:12,fontWeight:700,padding:"8px 16px",minHeight:44,cursor:"pointer",fontFamily:"inherit"}}>Retry</button>
        </div>
      ) : groups.length===0 ? (
        <div style={{fontSize:12,color:JT.text.tertiary,padding:"4px 2px"}}>You're not in any booths yet.</div>
      ) : groups.map(group=>(
        <div key={group.id} style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"8px 8px 8px 14px",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
            <span aria-hidden="true" style={{fontSize:18}}>{group.avatar_emoji||"🏒"}</span>
            <div style={{minWidth:0}}>
              <div style={{fontSize:14,fontWeight:800,color:JT.text.primary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{group.name}</div>
              <div style={{fontSize:11,color:JT.text.tertiary}}>{group.memberCount} member{group.memberCount!==1?"s":""}</div>
            </div>
          </div>
          <BookiesSafetyActions user={user} group={group} onChanged={onSafetyChange} limited/>
        </div>
      ))}
      {contactLine}
    </div>
  );

  // Fail closed: only an explicit "granted" or "outdated" (an earlier accepted version: still sharing) reaches the page.
  if (consent !== "granted" && consent !== "outdated") return (
    <div style={{padding:"20px 16px"}}>{contactLine}</div>
  );

  // A blocked member's slips never render here, whatever the server sends (pre-Codex FC F1).
  const visibleSlips = boardSlips.filter(slip => !(slip.userId && slip.userId !== user?.id && myBlocks.has(slip.userId)));

  return (
    <div style={{padding:"20px 16px",animation:"fadeUp .3s ease"}}>

      {consent === "outdated" && (
        <div style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"14px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{fontSize:13,color:JT.text.secondary,lineHeight:1.4}}>You're sharing your bets with your booth under the earlier terms. Review the updated terms.</div>
          <button onClick={()=>{setConsent("outdated-review");setConsentError(null);}} style={{flexShrink:0,background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.primary,borderRadius:8,fontSize:12,fontWeight:800,padding:"0 14px",minHeight:44,cursor:"pointer",fontFamily:"inherit"}}>Review</button>
        </div>
      )}

      {/* ── THE BOARD ── */}
      <div style={{...JT.type.label,color:JT.text.tertiary,letterSpacing:"2px",marginBottom:12}}>THE BOARD</div>

      {loading ? (
        [1,2,3].map(i=><div key={i} className="skeleton" style={{height:72,borderRadius:12,marginBottom:8,opacity:.5}}/>)
      ) : loadError ? (
        // UI critique item D: a load failure is ONE error card with Retry (load() clears both
        // lists, so this branch always fires); the Booths section below renders nothing on error.
        <div style={{background:JT.bg.well,border:`1px solid ${JT.status.lost.border}`,borderRadius:12,padding:"24px",textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:13,color:JT.status.lost.color,marginBottom:12}}>Couldn't reach The Bookies — try again in a moment.</div>
          <button onClick={()=>load()} style={{background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.secondary,borderRadius:8,fontSize:12,fontWeight:700,padding:"8px 16px",minHeight:44,cursor:"pointer",fontFamily:"inherit"}}>Retry</button>
        </div>
      ) : visibleSlips.length===0 ? (
        <div style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"24px",textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:13,color:JT.text.secondary}}>No active slips in your booths right now.</div>
        </div>
      ) : (
        <div style={{marginBottom:20}}>
          {visibleSlips.map((slip,i)=>{
            // #790: persisted finalResult wins so a settled push renders PUSH,
            // not a default-false recompute. Mirrors ResultsPage:78 / SlipTicket:51.
            // #1037 round 3 (Nova F1): shared league authority via betDisplayStatus --
            // finalResult still wins (#790); a root/game disagreement is a non-verdict.
            const st = betDisplayStatus(slip, games, { isMulti: slip.slipType==="parlay"||slip.slipType==="sgp", ...TRUST_DNP });
            const lm  = (Object.prototype.hasOwnProperty.call(SM, st) && SM[st]) || SM.pending;
            const odds = slip.combinedOdds||(slip.odds?fmtOdds(slip.odds):"—");
            // #1116: own-key lookup -- a stored '__proto__' must never resolve to an object.
            const sbLabel = getSportsbook(slip.sportsbook)?.name || (typeof slip.sportsbook === "string" && slip.sportsbook) || "—";
            const isMulti = slip.legs?.length>0;
            return (
              <div key={slip.id||i} style={{background:JT.bg.well,border:`1px solid ${lm.color}22`,borderRadius:12,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,fontWeight:800,color:lm.color,background:lm.color+"18",border:`1px solid ${lm.color}33`,borderRadius:4,padding:"2px 6px",letterSpacing:"1px"}}>{lm.label}</span>
                    {slip.userId===user?.id&&<span style={{fontSize:11,fontWeight:800,color:JT.brand.accent,background:`${JT.brand.cta}18`,border:`1px solid ${JT.brand.cta}33`,borderRadius:4,padding:"2px 6px",letterSpacing:"1px"}}>YOU</span>}
                    <span style={{fontSize:11,color:JT.text.tertiary}}>{sbLabel}</span>
                    <span style={{fontSize:11,color:JT.text.tertiary}}>·</span>
                    <span style={{fontSize:11,color:JT.text.tertiary,textTransform:"uppercase"}}>{slip.slipType||"—"}{isMulti?` · ${slip.legs.length}-LEG`:""}</span>
                  </div>
                  {isMulti ? (
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {slip.legs.slice(0,3).map((l,li)=>{
                        // #790: persisted leg_status (l.status) wins so a settled push
                        // chip stays consistent with the card, not a default-false recompute.
                        const lg  = games.find(g=>String(g.id)===String(l.gameId));
                        // #1655 B3b (P3-3): a not-graded-league leg on a FINAL game is the neutral
                        // NOT GRADED chip, never the amber MISSING DATA calcStatus gives it.
                        const ls  = l.status || (isNotGradedLegOnFinal(slip, l, lg ? [lg] : []) ? "not_graded"
                          : calcStatus({...l,liveStatVal:l.frozenStat??null}, lg, gradingLeague(slip, l, lg), false, TRUST_DNP));
                        const llm = (Object.prototype.hasOwnProperty.call(SM, ls) && SM[ls]) || SM.pending;
                        return <span key={li} style={{fontSize:11,color:llm.color,background:llm.color+"12",border:`1px solid ${llm.color}22`,borderRadius:4,padding:"2px 6px"}}>{llm.icon} {l.player||l.pick||l.rawSelectionText||"—"}</span>;
                      })}
                      {slip.legs.length>3&&<span style={{fontSize:11,color:JT.text.tertiary}}>+{slip.legs.length-3}</span>}
                    </div>
                  ) : (
                    <div style={{fontSize:12,color:JT.text.secondary,fontWeight:600}}>{slip.pick||"—"}</div>
                  )}
                </div>
                <div style={{textAlign:"right",marginLeft:12,flexShrink:0}}>
                  <div style={{fontSize:13,fontWeight:800,color:JT.text.primary}}>{odds}</div>
                  <div style={{fontSize:11,color:JT.text.tertiary}}>${slip.stake}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── THE BOOTHS ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{...JT.type.label,color:JT.text.tertiary,letterSpacing:"2px"}}>THE BOOTHS</div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>{setJoinOpen(v=>!v);setCreateOpen(false);}} style={{background:"none",border:`1px solid ${JT.border.mid}`,color:JT.text.secondary,borderRadius:6,fontSize:11,fontWeight:700,padding:"4px 10px",cursor:"pointer",fontFamily:"inherit"}}>Join</button>
          <button onClick={()=>{setCreateOpen(v=>!v);setJoinOpen(false);}} style={{background:"linear-gradient(135deg,#1d4ed8,#4f46e5)",border:"none",color:JT.text.primary,borderRadius:6,fontSize:11,fontWeight:800,padding:"4px 10px",cursor:"pointer",fontFamily:"inherit"}}>+ Create</button>
        </div>
      </div>

      {joinOpen&&(
        <div style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"14px",marginBottom:12}}>
          <div style={{fontSize:11,color:JT.text.secondary,fontWeight:700,marginBottom:8}}>Enter invite code</div>
          <div style={{display:"flex",gap:8}}>
            <input value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())} placeholder="6-CHAR CODE" maxLength={8}
              style={{flex:1,background:JT.bg.root,border:`1px solid ${JT.border.mid}`,borderRadius:8,padding:"8px 12px",color:JT.text.primary,fontSize:13,fontFamily:"inherit",outline:"none",letterSpacing:"2px"}}/>
            <button onClick={joinBooth} disabled={joining||!joinCode.trim()}
              style={{background:"linear-gradient(135deg,#1d4ed8,#4f46e5)",border:"none",color:JT.text.primary,borderRadius:8,fontSize:12,fontWeight:800,padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",opacity:(joining||!joinCode.trim())?0.5:1}}>
              {joining?"…":"Join"}
            </button>
          </div>
          {joinError&&<div style={{fontSize:11,color:JT.status.lost.color,marginTop:6}}>{joinError}</div>}
        </div>
      )}

      {createOpen&&(
        <div style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"14px",marginBottom:12}}>
          <div style={{fontSize:11,color:JT.text.secondary,fontWeight:700,marginBottom:8}}>New Booth</div>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <input value={newEmoji} onChange={e=>setNewEmoji(e.target.value)} maxLength={2}
              style={{width:42,background:JT.bg.root,border:`1px solid ${JT.border.mid}`,borderRadius:8,padding:"8px",color:JT.text.primary,fontSize:18,fontFamily:"inherit",outline:"none",textAlign:"center"}}/>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Booth name" maxLength={30}
              style={{flex:1,background:JT.bg.root,border:`1px solid ${JT.border.mid}`,borderRadius:8,padding:"8px 12px",color:JT.text.primary,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
          </div>
          <button onClick={createBooth} disabled={creating||!newName.trim()}
            style={{width:"100%",background:"linear-gradient(135deg,#1d4ed8,#4f46e5)",border:"none",color:JT.text.primary,borderRadius:8,fontSize:13,fontWeight:800,padding:"10px",cursor:"pointer",fontFamily:"inherit",opacity:(creating||!newName.trim())?0.5:1}}>
            {creating?"Creating…":"Create Booth"}
          </button>
          {createError&&<div style={{fontSize:11,color:JT.status.lost.color,marginTop:6}}>{createError}</div>}
        </div>
      )}

      {loading ? (
        [1,2].map(i=><div key={i} className="skeleton" style={{height:120,borderRadius:12,marginBottom:10,opacity:.5}}/>)
      ) : loadError ? null : groups.length===0 ? (
        <div style={{background:JT.bg.well,border:`1px dashed ${JT.border.mid}`,borderRadius:12,padding:"32px 24px",textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:10}}>🏟</div>
          <div style={{fontSize:14,fontWeight:800,color:JT.text.primary,marginBottom:6}}>No booths yet</div>
          <div style={{fontSize:12,color:JT.text.secondary,lineHeight:1.5}}>Create a booth or join one with an invite code to track your crew's slips.</div>
        </div>
      ) : groups.map(group=>(
        <div key={group.id} style={{background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:12,padding:"14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:38,height:38,borderRadius:10,background:JT.bg.card,border:`1px solid ${JT.border.hero}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>
                {group.avatar_emoji||"🏒"}
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:JT.text.primary}}>{group.name}</div>
                <div style={{fontSize:11,color:JT.text.tertiary}}>{group.memberCount} member{group.memberCount!==1?"s":""}</div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:2}}>
              <button onClick={async()=>{await copyText(group.invite_code);setCopied(group.id);setTimeout(()=>setCopied(null),2000);}}
                style={{background:JT.bg.card,border:`1px solid ${JT.border.mid}`,color:copied===group.id?JT.status.won.color:JT.text.tertiary,borderRadius:6,fontSize:11,fontWeight:700,padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",letterSpacing:"1px",transition:"color .2s",minWidth:64}}>
                {copied===group.id?"✓ COPIED":group.invite_code}
              </button>
              <BookiesSafetyActions user={user} group={group} onChanged={onSafetyChange}/>
            </div>
          </div>
          {group.leaderboard.length>0&&(
            <div style={{borderTop:`1px solid ${JT.border.default}`,paddingTop:10}}>
              <div style={{...JT.type.label,color:JT.text.tertiary,letterSpacing:"1.5px",marginBottom:8}}>LEADERBOARD</div>
              {group.leaderboard.map(([uid,stats],idx)=>{
                const isMe    = uid===user?.id;
                const winRate = stats.total?Math.round((stats.won/stats.total)*100):0;
                const isBlocked = !isMe && myBlocks.has(uid);
                const hidden  = !isMe && (isBlocked || !stats.sharing);
                return (
                  <div key={uid} data-member-row style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"2px 0",borderBottom:idx<group.leaderboard.length-1?`1px solid ${JT.bg.card}`:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:12,width:20}}>{MEDALS[idx]||`#${idx+1}`}</span>
                      <span style={{fontSize:12,fontWeight:700,color:isMe?JT.brand.accent:JT.text.secondary}}>{isMe?"You":`Member ${idx+1}`}</span>
                    </div>
                    <div style={{display:"flex",gap:12,alignItems:"center",minHeight:44}}>
                      {hidden ? (
                        <span style={{fontSize:11,color:JT.text.tertiary}}>{isBlocked?"Blocked":"Not sharing yet"}</span>
                      ) : (<>
                        <span style={{fontSize:11,color:JT.text.tertiary}}>{winRate}% W</span>
                        <span style={{fontSize:12,fontWeight:800,color:stats.profit>=0?JT.status.won.color:JT.status.lost.color}}>
                          {stats.profit>=0?"+":""}{stats.profit.toFixed(0)}
                        </span>
                      </>)}
                      {!isMe && <BookiesSafetyActions user={user} group={group} memberId={uid} blocked={isBlocked} canBlock={blocksEnforced} onChanged={onSafetyChange}/>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      {contactLine}
    </div>
  );
}
